import { useEffect, useRef } from "react"
import { useFormContext } from "react-hook-form"
import type { ApplicableBetterDocUsage } from "@/modules/patients/detail/better-doc/hooks/useApplicableBetterDocUsage"
import { canBetterDocUsageStillChange } from "@/modules/patients/detail/better-doc/lib/can-better-doc-usage-still-change"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import { SHOULD_VALIDATE_AND_DIRTY } from "@/modules/patients/detail/progress-note/point-of-care/constants"
import { isCareProviderPreselectable } from "@/modules/patients/detail/progress-note/point-of-care/lib/is-care-provider-preselectable"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"

type UsePreselectCareProviderOnceSelectionIsUnblockedParameters = {
  applicableBetterDocUsage: ApplicableBetterDocUsage
  preselectedCareProviderId: string | null
}

export const usePreselectCareProviderOnceSelectionIsUnblocked = ({
  applicableBetterDocUsage,
  preselectedCareProviderId,
}: UsePreselectCareProviderOnceSelectionIsUnblockedParameters): void => {
  const { getValues, setValue } = useFormContext<PointOfCareFormType>()
  const { betterDocUsage, missingConsultationDetailsThatCanChangeBetterDocUsage } = applicableBetterDocUsage
  const isCareProviderSelectionBlocked = canBetterDocUsageStillChange(
    missingConsultationDetailsThatCanChangeBetterDocUsage,
  )
  const wasCareProviderSelectionBlockedRef = useRef(false)

  useEffect(() => {
    if (betterDocUsage === null) return
    const wasCareProviderSelectionBlocked = wasCareProviderSelectionBlockedRef.current
    wasCareProviderSelectionBlockedRef.current = isCareProviderSelectionBlocked

    if (isCareProviderSelectionBlocked || isBetterDocRequired(betterDocUsage)) return
    if (!wasCareProviderSelectionBlocked) return

    if (preselectedCareProviderId && isCareProviderPreselectable(getValues)) {
      setValue("careProviderId", preselectedCareProviderId, SHOULD_VALIDATE_AND_DIRTY)
    }
  }, [betterDocUsage, isCareProviderSelectionBlocked, preselectedCareProviderId, getValues, setValue])
}
